import React, { useEffect, useState, useCallback } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/LoadingSpinner';
import Swal from 'sweetalert2';
import i18n from '../i18n';
import api from "../api";

function SurveyTab({ loadingBarRef }) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('createSurvey');
    const [language, setLanguage] = useState(i18n.language);
    const [surveyData, setSurveyData] = useState({
        active: true,
        groups: [
            {
                title: '',
                questions: { en: '', ru: '', ua: '', ko: '', fr: '' },
                options: [
                    { text: { en: '', ru: '', ua: '', ko: '', fr: '' }, logicalGroup: 1 },
                    { text: { en: '', ru: '', ua: '', ko: '', fr: '' }, logicalGroup: 2 }
                ]
            }
        ]
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [surveyList, setSurveyList] = useState([]);
    const [translating, setTranslating] = useState(false);

    const handleTranslate = async () => {
        const fields = {};
        surveyData.groups.forEach((group, gi) => {
            fields[`group_${gi}_question`] = group.questions.en || '';
            group.options.forEach((option, oi) => {
                fields[`group_${gi}_option_${oi}`] = option.text.en || '';
            });
        });
        if (!Object.values(fields).some((v) => v.trim())) return;

        setTranslating(true);
        if (loadingBarRef.current) loadingBarRef.current.continuousStart();
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/api/admin/translate`, { fields });
            const translations = response.data || {};
            setSurveyData((prev) => {
                const newGroups = prev.groups.map((group, gi) => {
                    const newQuestions = { ...group.questions };
                    const newOptions = group.options.map((option) => ({ ...option, text: { ...option.text } }));
                    ['ru', 'ua', 'ko', 'fr'].forEach((lang) => {
                        const tr = translations[lang] || {};
                        newQuestions[lang] = tr[`group_${gi}_question`] ?? '';
                        newOptions.forEach((option, oi) => {
                            option.text[lang] = tr[`group_${gi}_option_${oi}`] ?? '';
                        });
                    });
                    return { ...group, questions: newQuestions, options: newOptions };
                });
                return { ...prev, groups: newGroups };
            });
        } catch (error) {
            Swal.fire({
                text: t('news.translateError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonText: t('posts.ok'),
                customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
            });
        } finally {
            setTranslating(false);
            if (loadingBarRef.current) loadingBarRef.current.complete();
        }
    };

    const handleChange = (field, value, groupIndex, lang, optionIndex = null) => {
        setSurveyData((prevData) => {
            const newGroups = [...prevData.groups];
            if (field === 'title') {
                newGroups[groupIndex].title = value;
            } else if (field === 'questions') {
                newGroups[groupIndex].questions[lang] = value;
            } else if (field === 'options' && optionIndex !== null) {
                newGroups[groupIndex].options[optionIndex].text[lang] = value;
            }
            return { ...prevData, groups: newGroups };
        });
    };

    const addGroup = () => {
        setSurveyData((prevData) => ({
            ...prevData,
            groups: [
                ...prevData.groups,
                {
                    title: '',
                    questions: { en: '', ru: '', ua: '', ko: '', fr: '' },
                    options: [
                        { text: { en: '', ru: '', ua: '', ko: '', fr: '' }, logicalGroup: 1 },
                        { text: { en: '', ru: '', ua: '', ko: '', fr: '' }, logicalGroup: 2 }
                    ]
                }
            ]
        }));
    };

    const removeGroup = (groupIndex) => {
        setSurveyData((prevData) => ({
            ...prevData,
            groups: prevData.groups.filter((_, index) => index !== groupIndex)
        }));
    };

    const addOption = (groupIndex) => {
        setSurveyData((prevData) => {
            const newGroups = prevData.groups.map((group, idx) => {
                if (idx === groupIndex) {
                    const newOption = {
                        text: { en: '', ru: '', ua: '', ko: '', fr: '' },
                        logicalGroup: group.options.length + 1
                    };
                    return { ...group, options: [...group.options, newOption] };
                }
                return group;
            });
            return { ...prevData, groups: newGroups };
        });
    };

    const removeOption = (groupIndex, optionIndex) => {
        setSurveyData((prevData) => {
            const newGroups = [...prevData.groups];
            if (newGroups[groupIndex].options.length > 2) {
                newGroups[groupIndex].options = newGroups[groupIndex].options.filter((_, index) => index !== optionIndex);
                newGroups[groupIndex].options.forEach((option, index) => {
                    option.logicalGroup = index + 1;
                });
            }
            return { ...prevData, groups: newGroups };
        });
    };

    const validateSurveyFields = () => {
        const errors = [];
        surveyData.groups.forEach((group, groupIndex) => {
            if (!group.title.trim()) {
                errors.push(t('survey.validationError.groupTitle', { groupIndex: groupIndex + 1 }));
            }
            if (group.options.length < 2) {
                errors.push(t('survey.validationError.minOptions', { groupIndex: groupIndex + 1 }));
            }
            for (const lang of ['en', 'ru', 'ua', 'ko', 'fr']) {
                if (!group.questions[lang].trim()) {
                    errors.push(t('survey.validationError.question', { lang: t(`languages.${lang}`), groupIndex: groupIndex + 1 }));
                }
                group.options.forEach((option, optionIndex) => {
                    if (!option.text[lang].trim()) {
                        errors.push(t('survey.validationError.option', { lang: t(`languages.${lang}`), groupIndex: groupIndex + 1, optionIndex: optionIndex + 1 }));
                    }
                });
            }
        });
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateSurveyFields();
        if (validationErrors.length > 0) {
            Swal.fire({
                title: t('survey.validationError.titleHeader'),
                html: `<ul>${validationErrors.map(err => `<li>${err}</li>`).join('')}</ul>`,
                icon: 'error',
                confirmButtonText: t('posts.ok'),
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
            });
            return;
        }

        Swal.fire({
            text: t('survey.confirmSubmit'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ffcc00',
            customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button', cancelButton: 'swal2-dark-button' }
        }).then(async (result) => {
            if (result.isConfirmed) {
                setIsSubmitting(true);
                setIsLoading(true);
                if (loadingBarRef.current) loadingBarRef.current.continuousStart();

                try {
                    const accessToken = localStorage.getItem('access_token');
                    const response = await api.post(`${process.env.REACT_APP_API_URL}/api/admin/survey/create`, surveyData, {
                        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
                    });

                    if (response.status === 201) {
                        Swal.fire({
                            text: t('survey.success'),
                            icon: 'success',
                            background: '#1a1a1a',
                            color: '#b3b3b3',
                            iconColor: '#00ff00',
                            confirmButtonText: t('posts.ok'),
                            customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
                        });
                        setSurveyData({
                            active: true,
                            groups: [{
                                title: '',
                                questions: { en: '', ru: '', ua: '', ko: '', fr: '' },
                                options: [
                                    { text: { en: '', ru: '', ua: '', ko: '', fr: '' }, logicalGroup: 1 },
                                    { text: { en: '', ru: '', ua: '', ko: '', fr: '' }, logicalGroup: 2 }
                                ]
                            }]
                        });
                        if (activeTab === 'manageSurveys') fetchSurveyList();
                    }
                } catch (error) {
                    Swal.fire({
                        text: t('survey.error'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonText: t('posts.ok'),
                        customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
                    });
                } finally {
                    setIsSubmitting(false);
                    setIsLoading(false);
                    if (loadingBarRef.current) loadingBarRef.current.complete();
                }
            }
        });
    };

    const fetchSurveyList = useCallback(async () => {
        setIsLoading(true);
        if (loadingBarRef.current) loadingBarRef.current.continuousStart();
        try {
            const accessToken = localStorage.getItem('access_token');
            const response = await api.get(`${process.env.REACT_APP_API_URL}/api/admin/survey/list`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            setSurveyList(response.data);
        } catch (error) {
            Swal.fire({
                text: t('survey.fetchError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonText: t('posts.ok'),
                customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
            });
        } finally {
            setIsLoading(false);
            if (loadingBarRef.current) loadingBarRef.current.complete();
        }
    }, [loadingBarRef, t]);

    const deleteSurvey = async (surveyId) => {
        Swal.fire({
            text: t('survey.confirmDelete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ffcc00',
            customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button', cancelButton: 'swal2-dark-button' }
        }).then(async (result) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                if (loadingBarRef.current) loadingBarRef.current.continuousStart();
                try {
                    const accessToken = localStorage.getItem('access_token');
                    const response = await api.delete(`${process.env.REACT_APP_API_URL}/api/admin/survey/${surveyId}/delete`, {
                        headers: { 'Authorization': `Bearer ${accessToken}` }
                    });
                    if (response.status === 200) {
                        Swal.fire({
                            text: t('survey.deleteSuccess'),
                            icon: 'success',
                            background: '#1a1a1a',
                            color: '#b3b3b3',
                            iconColor: '#00ff00',
                            confirmButtonText: t('posts.ok'),
                            customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
                        });
                        setSurveyList(surveyList.filter(survey => survey.id !== surveyId));
                    }
                } catch (error) {
                    Swal.fire({
                        text: t('survey.deleteError'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonText: t('posts.ok'),
                        customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
                    });
                } finally {
                    setIsLoading(false);
                    if (loadingBarRef.current) loadingBarRef.current.complete();
                }
            }
        });
    };

    const toggleSurveyActive = async (surveyId, currentStatus) => {
        const action = currentStatus ? t('survey.deactivate') : t('survey.activate');
        Swal.fire({
            text: t('survey.confirmToggle', { action }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ffcc00',
            customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button', cancelButton: 'swal2-dark-button' }
        }).then(async (result) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                if (loadingBarRef.current) loadingBarRef.current.continuousStart();
                try {
                    const accessToken = localStorage.getItem('access_token');
                    const response = await api.post(`${process.env.REACT_APP_API_URL}/api/admin/survey/${surveyId}/toggle`, {}, {
                        headers: { 'Authorization': `Bearer ${accessToken}` }
                    });
                    if (response.status === 200) {
                        Swal.fire({
                            text: t('survey.toggleSuccess', { action }),
                            icon: 'success',
                            background: '#1a1a1a',
                            color: '#b3b3b3',
                            iconColor: '#00ff00',
                            confirmButtonText: t('posts.ok'),
                            customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
                        });
                        setSurveyList(surveyList.map(survey =>
                            survey.id === surveyId
                                ? { ...survey, active: !currentStatus }
                                : currentStatus ? { ...survey, active: false } : survey
                        ));
                    }
                } catch (error) {
                    Swal.fire({
                        text: t('survey.toggleError'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonText: t('posts.ok'),
                        customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
                    });
                } finally {
                    setIsLoading(false);
                    if (loadingBarRef.current) loadingBarRef.current.complete();
                }
            }
        });
    };

    useEffect(() => {
        if (activeTab === 'manageSurveys') {
            fetchSurveyList();
        }
    }, [activeTab, fetchSurveyList]);

    return (
        <div>
            {isLoading ? (
                <div className="text-center">
                    <LoadingSpinner />
                    <p>{t('survey.loading')}</p>
                </div>
            ) : (
                <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} id="survey-management-tabs" className="mb-3">
                    <Tab eventKey="createSurvey" title={t('survey.createSurvey')}>
                        <div>
                            <div className="survey-status">
                                <label>{t('survey.active')}</label>
                                <input
                                    type="checkbox"
                                    checked={surveyData.active}
                                    onChange={(e) => setSurveyData({ ...surveyData, active: e.target.checked })}
                                />
                            </div>
                            {surveyData.groups.map((group, groupIndex) => (
                                <div key={groupIndex} className="group-section">
                                    <h3>{t('survey.group')} {groupIndex + 1}</h3>
                                    <div className="group-actions">
                                        {surveyData.groups.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeGroup(groupIndex)}
                                                className="remove-group-button"
                                            >
                                                {t('survey.removeGroup')}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => addOption(groupIndex)}
                                            className="add-option-button"
                                        >
                                            {t('survey.addOption')}
                                        </button>
                                    </div>
                                    <div className="form-group">
                                        <label>{t('survey.groupTitle')}</label>
                                        <input
                                            type="text"
                                            value={group.title}
                                            onChange={(e) => handleChange('title', e.target.value, groupIndex)}
                                        />
                                    </div>
                                    <Tabs activeKey={language} onSelect={(k) => setLanguage(k)} id={`group-${groupIndex}-language-tabs`} className="mb-3">
                                        {['en', 'ru', 'ua', 'ko', 'fr'].map((lang) => (
                                            <Tab eventKey={lang} title={t(`languages.${lang}`)} key={lang}>
                                                <div className="form-group">
                                                    <label>{t('survey.question')}:</label>
                                                    <textarea
                                                        value={group.questions[lang]}
                                                        onChange={(e) => handleChange('questions', e.target.value, groupIndex, lang)}
                                                    />
                                                </div>
                                                {group.options.map((option, optionIndex) => (
                                                    <div key={optionIndex} className="option-section">
                                                        <div className="option-header">
                                                            <label>{t('survey.option')} {optionIndex + 1}:</label>
                                                        </div>
                                                        <div className="option-content">
                                                            <input
                                                                type="text"
                                                                value={option.text[lang]}
                                                                onChange={(e) => handleChange('options', e.target.value, groupIndex, lang, optionIndex)}
                                                            />
                                                            {group.options.length > 2 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeOption(groupIndex, optionIndex)}
                                                                    className="remove-option-button"
                                                                >
                                                                    {t('survey.removeOption')}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </Tab>
                                        ))}
                                    </Tabs>
                                </div>
                            ))}
                            <button type="button" onClick={addGroup} className="add-group-button">
                                {t('survey.addGroup')}
                            </button>
                            <div className="form-buttons">
                                <button type="button" onClick={handleTranslate} className="preview-button" disabled={translating}>
                                    {translating ? t('news.translating') : t('news.translate')}
                                </button>
                                <button onClick={handleSubmit} className="submit-button" disabled={isSubmitting}>
                                    {isSubmitting ? t('survey.submitting') : t('survey.submit')}
                                </button>
                            </div>
                        </div>
                    </Tab>
                    <Tab eventKey="manageSurveys" title={t('survey.manageSurveys')}>
                        <div className="survey-list">
                            {surveyList.length === 0 ? (
                                <p>{t('survey.noSurveys')}</p>
                            ) : (
                                <div className="admin-survey-container">
                                    {surveyList.map((survey) => (
                                        <div key={survey.id} className="survey-item">
                                            <div className="survey-details">
                                                <h4>{t('survey.survey')} #{survey.id}</h4>
                                                <div className="survey-info">
                                                    <p>{t('survey.createdAt')}: {new Date(survey.created_at).toLocaleString()}</p>
                                                    <p>{t('survey.status')}: {survey.active ? t('survey.active') : t('survey.inactive')}</p>
                                                </div>
                                                <div className="survey-groups">
                                                    <strong>{t('survey.groups')}:</strong>
                                                    <ul>
                                                        {survey.groups && survey.groups.length > 0 ? (
                                                            survey.groups.map((group, index) => (
                                                                <li key={index}>{group.title}</li>
                                                            ))
                                                        ) : (
                                                            <li>{t('survey.noGroups')}</li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                            <div className="survey-actions">
                                                <button
                                                    className={`toggle-button ${survey.active ? 'deactivate' : 'activate'}`}
                                                    onClick={() => toggleSurveyActive(survey.id, survey.active)}
                                                >
                                                    {survey.active ? t('survey.deactivate') : t('survey.activate')}
                                                </button>
                                                <button
                                                    className="survey-delete-button"
                                                    onClick={() => deleteSurvey(survey.id)}
                                                >
                                                    {t('survey.delete')}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Tab>
                </Tabs>
            )}
        </div>
    );
}

export default SurveyTab;